/**
 * KAN-1003: Пользовательское соглашение сервиса "Сметчик".
 * Основа: утверждённое владельцем соглашение content-zavod (редакция 01.11.2026),
 * адаптировано под модель "кошелёк и оплата по расходу" и оплату через ЮKassa.
 * Сторона договора: ИП с ИНН и ОГРНИП (требование ЮKassa), без паспорта и адреса.
 * Лимит одного платежа в тексте не зашит: границы пополнения на странице цен из API.
 */
import type { LegalDocument } from './types';
import {
  LEGAL_EDITION,
  PRICES_LINK,
  PRIVACY_LINK,
  SELLER,
  SELLER_REQUISITES,
  SERVICE_NAME,
  SERVICE_SITE,
  SUPPORT_EMAIL_LINK,
  SUPPORT_PHONE_LINK,
} from './support';

export const TERMS_DOCUMENT: LegalDocument = {
  title: 'Пользовательское соглашение',
  edition: LEGAL_EDITION,
  sections: [
    {
      heading: '1. Общие положения',
      paragraphs: [
        `1.1. Соглашение регулирует использование сервиса "${SERVICE_NAME}": сайт и личный кабинет ${SERVICE_SITE} (далее Сервис).`,
        [
          `1.2. Сервис предоставляет ${SELLER.fullName} (${SELLER_REQUISITES}), далее Администрация сервиса или Администрация. Связь с Администрацией: `,
          SUPPORT_EMAIL_LINK,
          ', телефон ',
          SUPPORT_PHONE_LINK,
          '.',
        ],
        '1.3. Соглашение является публичной офертой. Пользователь принимает его (акцепт) при регистрации в Сервисе, а также при пополнении баланса. Не согласен с условиями: Сервисом не пользуется.',
        ['1.4. Вместе с Соглашением действуют ', PRIVACY_LINK, ' и страница ', PRICES_LINK, '.'],
      ],
    },
    {
      heading: '2. Предмет соглашения',
      paragraphs: [
        '2.1. Сервис: облачная платформа (SaaS) ИИ-операторов для отделов продаж. ИИ-оператор по настройкам пользователя ведёт переписку с клиентами в мессенджерах, совершает и принимает звонки, ведёт CRM, работает с базами контактов пользователя и передаёт заинтересованных клиентов менеджерам.',
        '2.2. Тарифов нет, все функции Сервиса открыты. Пользователь платит только за фактический расход ресурсов по ценам раздела 4.',
        '2.3. Ответы и речь ИИ-оператора создаёт нейросеть. Сервис не гарантирует определённого числа ответов клиентов, звонков, заявок или продаж.',
        '2.4. Использовать Сервис для создания и распространения материалов, запрещённых законом, нельзя. За содержание сообщений, сценариев и материалов, которые ИИ-оператор отправляет по настройкам пользователя, отвечает пользователь.',
      ],
    },
    {
      heading: '3. Обязанности пользователя',
      paragraphs: [
        '3.1. Пользователь отвечает за законность своих баз контактов, за наличие согласий получателей на сообщения и звонки, за соблюдение законодательства о рекламе, о связи и о персональных данных.',
        '3.2. Запрещено использовать Сервис для спама, мошенничества, оскорблений, рекламы запрещённых товаров, обхода ограничений мессенджеров и операторов связи, а также в иных противоправных целях.',
        '3.3. Пользователь хранит данные для входа в кабинет в тайне и отвечает за действия, совершённые из его кабинета.',
      ],
    },
    {
      heading: '4. Цены и порядок оплаты',
      paragraphs: [
        [
          '4.1. Оплата устроена как кошелёк: пользователь пополняет рублёвый баланс кабинета, Сервис списывает с баланса стоимость фактического расхода: работы текстового ИИ, голосового ИИ и минут телефонии. Актуальные цены с наценкой платформы и границы суммы пополнения опубликованы на странице ',
          PRICES_LINK,
          '.',
        ],
        '4.2. Способы оплаты: банковская карта или СБП через платёжный сервис ЮKassa. Кассовый чек по 54-ФЗ направляется на e-mail, указанный при оплате.',
        ['4.3. Юридические лица и предприниматели могут оплатить по счёту (безналичный расчёт): счёт выставляется по запросу в поддержку ', SUPPORT_EMAIL_LINK, '.'],
        '4.4. Пополнения и списания видны в кабинете в разделе "Баланс". Когда баланс заканчивается, платные действия (ответы ИИ, рассылка, звонки) приостанавливаются до пополнения.',
        '4.5. Новые цены действуют с момента публикации на странице цен. Уже списанные суммы не пересчитываются.',
      ],
    },
    {
      heading: '5. Возврат',
      paragraphs: [
        '5.1. Неизрасходованный остаток баланса возвращается деньгами по обращению в поддержку тем же способом, которым была оплата, в течение 10 рабочих дней. Израсходованные средства не возвращаются.',
        '5.2. Перед обращением в банк для отмены платежа пользователь сначала пишет в поддержку: так вопрос решается быстрее.',
      ],
    },
    {
      heading: '6. Ограничение доступа',
      paragraphs: [
        '6.1. Администрация может приостановить доступ при нарушении Соглашения, злоупотреблениях или по требованию закона и платёжных сервисов. Неизрасходованный остаток баланса в этом случае возвращается, кроме случаев мошенничества.',
      ],
    },
    {
      heading: '7. Ответственность',
      paragraphs: [
        '7.1. Сервис предоставляется "как есть". Администрация не отвечает за сбои поставщиков нейросетей, мессенджеров и операторов связи, за блокировки аккаунтов мессенджеров из-за действий пользователя, за упущенную выгоду.',
        '7.2. Ответственность Администрации перед пользователем ограничена суммой, внесённой пользователем на баланс за последние 30 дней.',
      ],
    },
    {
      heading: '8. Персональные данные',
      paragraphs: [
        [
          '8.1. Персональные данные обрабатываются по Федеральному закону от 27.07.2006 N 152-ФЗ "О персональных данных" в порядке, описанном в документе ',
          PRIVACY_LINK,
          '.',
        ],
      ],
    },
    {
      heading: '9. Изменения',
      paragraphs: [
        '9.1. Новая редакция Соглашения публикуется на этой странице и действует с момента публикации. Продолжая пользоваться Сервисом, пользователь принимает новую редакцию.',
      ],
    },
    {
      heading: '10. Реквизиты и поддержка',
      paragraphs: [
        `10.1. ${SELLER.fullName}, ${SELLER_REQUISITES}.`,
        ['10.2. Email ', SUPPORT_EMAIL_LINK, ', телефон ', SUPPORT_PHONE_LINK, '.'],
      ],
    },
  ],
};
